import { trigger, state, style, transition, animate } from '@angular/animations';
import { Component, ContentChild, Input, TemplateRef, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class TableComponent {

  @Input() dataArr: any[] = []
  @Input() dataMap: any = {};
  // Optional opt-in: per-column header filters. Keyed by the dataMap label (= displayed column).
  // Shape: { 'School': { type: 'select', options: string[] }, 'Name': { type: 'text' } }.
  // When null/undefined the table behaves exactly as before (global search only) — other grids
  // are unaffected.
  @Input() columnFilters: any = null;
  @ContentChild('actionsItems') actionsItems!: TemplateRef<any>;
  @ContentChild('expandedView') expandedView!: TemplateRef<any>;

  dataSource: MatTableDataSource<any> = new MatTableDataSource(this.dataArr);
  displayedColumns: string[] = [];
  columnsToDisplayWithExpand:string[] = [];
  filterRowColumns: string[] = [];

  // Combined filter state serialized into dataSource.filter when columnFilters is enabled.
  filterState: { global: string; columns: { [key: string]: string } } = { global: '', columns: {} };

  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
  @ViewChild(MatSort) sort: MatSort | undefined;


  constructor() { }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator!;
    this.dataSource.sort = this.sort!;
  }

  ngOnChanges() {
    const mappedData = this.dataMapping(this.dataArr);
    this.dataSource = new MatTableDataSource(mappedData);
    this.dataSource.paginator = this.paginator!;
    this.dataSource.sort = this.sort!;

    this.displayedColumns = Object.keys(this.dataMap);
    if(!!this.actionsItems){
      this.columnsToDisplayWithExpand = [...this.displayedColumns, 'actionsItems'];
    }else{
      this.columnsToDisplayWithExpand = [...this.displayedColumns];
    }

    // Parallel set of column-def names for the optional filter header row.
    this.filterRowColumns = this.columnsToDisplayWithExpand.map(c => c + '__filter');

    // Only install the custom predicate when column filters are in use; otherwise leave the
    // default MatTableDataSource behavior so the global search on other grids is unchanged.
    if (this.columnFilters) {
      this.dataSource.filterPredicate = (row: any, filter: string) => this.matchRow(row, filter);
      this.applyFilterState();
    }
  }

  // Build the active filter string and push it to the data source. Always non-empty JSON so the
  // predicate runs; an all-empty state simply matches every row.
  applyFilterState() {
    this.dataSource.filter = JSON.stringify(this.filterState);
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  setColumnFilter(column: string, value: string) {
    this.filterState.columns[column] = value || '';
    this.applyFilterState();
  }

  // Clear every per-column filter and the global search. The header controls are one-way bound
  // to filterState, so they reset visually on the next change-detection pass.
  clearFilters() {
    this.filterState = { global: '', columns: {} };
    if (this.columnFilters) {
      this.applyFilterState();
    } else {
      this.dataSource.filter = '';
      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }

  private matchRow(row: any, filter: string): boolean {
    let state: { global: string; columns: { [key: string]: string } };
    try {
      state = JSON.parse(filter);
    } catch {
      // Fallback: treat as a plain global substring (defensive — shouldn't happen here).
      const f = (filter || '').trim().toLowerCase();
      return !f || this.displayedColumns.some(c => (row[c] ?? '').toString().toLowerCase().includes(f));
    }

    // Per-column filters (exact match for selects, substring for text inputs).
    for (const col of Object.keys(state.columns || {})) {
      const want = (state.columns[col] || '').toString().toLowerCase();
      if (!want) continue;
      const cell = (row[col] ?? '').toString().toLowerCase();
      const type = this.columnFilters?.[col]?.type;
      if (type === 'text') {
        if (!cell.includes(want)) return false;
      } else {
        if (cell !== want) return false;
      }
    }

    // Global search across all displayed columns.
    const g = (state.global || '').trim().toLowerCase();
    if (g) {
      const hay = this.displayedColumns.map(c => (row[c] ?? '')).join(' ').toLowerCase();
      if (!hay.includes(g)) return false;
    }
    return true;
  }

  getValueFromObject(obj: any, key: string): any {
    return key.split('.').reduce((o, k) => (o || {})[k], obj);
  }

  dataMapping(data: any[]) {
    return data.map(element => {
      Object.keys(this.dataMap).forEach((key) => {
          element[key] = this.getValueFromObject(element, this.dataMap[key]);
      }, {} as any);
      element.isExpanded =false;
      return element;
    });
  }

  applyFilter(filterValue: string) {
    if (this.columnFilters) {
      this.filterState.global = filterValue;
      this.applyFilterState();
      return;
    }
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

}
